/* eslint-disable react/prop-types */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  getDoc,
  getDocs,
  collection,
  setDoc,
  doc,
  query,
  orderBy,
  limit,
  startAfter,
  documentId,
} from 'firebase/firestore';

import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';

const context = createContext(null);

function FirebaseProvider({ children, config }) {
  const [currentUser, setCurrentUser] = useState();
  const [authEnabled, setAuthEnabled] = useState(false);
  const app = useMemo(() => (config ? initializeApp(config) : undefined), [config]);
  const db = useMemo(() => (app ? getFirestore() : undefined), [app]);
  const auth = useMemo(() => (app ? getAuth() : undefined), [app]);

  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      setCurrentUser(user);
      return;
    }
    const querySnapshot = await getDoc(doc(db, 'internal', user.uid));
    Object.assign(user, {
      internal: { roles: [], ...querySnapshot.data() },
    });
    setCurrentUser(user);
  });

  const loginWithGoogle = useCallback(() => {
    if (!auth) return;
    (async () => {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    })();
  }, [auth]);

  const logout = useCallback(() => {
    signOut(auth);
  }, [auth]);

  const value = useMemo(() => ({
    config,
    app,
    db,
    authEnabled,
    setAuthEnabled,
    currentUser,
    logout,
    loginWithGoogle,
  }), [config, app, db, authEnabled, currentUser, logout, loginWithGoogle]);

  return (
    <context.Provider value={value}>
      {children}
    </context.Provider>
  );
}

export function useFirebaseContext() {
  return useContext(context);
}

export function useFirebaseApp() {
  const { app } = useFirebaseContext();
  return app;
}

export function useFirebaseDb() {
  const { db } = useFirebaseContext();
  return db;
}

export function useFirebaseCollection(name, options = {}) {
  const [response, setResponse] = useState({
    loading: false,
    data: null,
    error: null,
  });
  const { db } = useFirebaseContext();

  const getData = useCallback(async () => {
    if (!name || !db || options.skip) return;
    setResponse({
      loading: true,
      data: null,
      error: null,
    });
    try {
      const querySnapshot = await getDocs(collection(db, name));
      const data = [];
      querySnapshot.forEach((item) => {
        const payload = item.data();
        if (payload.enabled === false && !options.all) return;
        data.push({
          id: item.id,
          ...payload,
        });
      });
      setResponse({
        loading: false,
        data,
        error: null,
      });
    } catch (error) {
      setResponse({
        loading: false,
        data: null,
        error,
      });
    }
  }, [db, name, options.skip]);

  useEffect(() => {
    (async () => {
      getData();
    })();
  }, [getData]);

  const create = useCallback((data) => {
    setDoc(doc(db, name, btoa((new Date()).valueOf())), data);
  }, [db, name]);

  const update = useCallback((id, data) => setDoc(doc(db, name, id), data), [db, name]);

  return {
    ...response,
    create,
    update,
    refetch: getData,
  };
}

/**
 * Cursor-based pagination over a Firestore collection.
 *
 * Each page yields exactly `pageSize` *visible* items. Because items can be
 * hidden client-side (disabled, or missing content for the active language),
 * a fixed `limit(pageSize)` would return fewer and fewer cards per page as the
 * proportion of hidden docs grows. To avoid that, we keep reading blocks with
 * `startAfter` and accumulate only the items that pass `filter` until we have a
 * full page (or the collection is exhausted). The cursor saved for the next
 * page is the snapshot of the last *kept* item, so no doc is skipped.
 *
 * There is no total page count (Firestore has no cheap count): we only know a
 * next page exists when the current one filled up. The pager grows as the user
 * moves forward (numbered pages, no total).
 *
 * Ordering defaults to document id ascending — it matches the order the view
 * had before (Firestore default `__name__` asc) and uses the default index, so
 * it needs no manually created Firestore composite index. Pages already visited
 * are cached in memory; changing `filterKey` (e.g. the language) resets them.
 */
export function useFirebasePaginatedCollection(name, options = {}) {
  const {
    pageSize = 9,
    orderField = documentId(),
    direction = 'asc',
    constraints = [],
    filter,
    filterKey = '',
    skip = false,
  } = options;

  const { db } = useFirebaseContext();

  // latest filter / constraints without making them dependencies (usually inline)
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const constraintsRef = useRef(constraints);
  constraintsRef.current = constraints;

  // source of truth for fetch logic — refs stay fresh across awaits / resets
  const pagesRef = useRef({}); // { [pageIndex]: data[] }
  const cursorsRef = useRef({}); // { [pageIndex]: lastKeptSnapshot }
  const moreRef = useRef({}); // { [pageIndex]: bool }

  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [, setVersion] = useState(0); // bump to re-render after ref mutations
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const fetchPage = useCallback(async (pageIndex) => {
    if (!name || !db || skip) return;
    if (pagesRef.current[pageIndex]) { setPage(pageIndex); return; } // cached
    if (pageIndex > 0 && !cursorsRef.current[pageIndex - 1]) return; // no cursor yet

    setLoading(true);
    setError(null);
    try {
      const f = filterRef.current;
      let scanCursor = pageIndex > 0 ? cursorsRef.current[pageIndex - 1] : null;
      const kept = []; // { snap, data }
      let exhausted = false;

      while (kept.length < pageSize && !exhausted) {
        const cons = [...constraintsRef.current, orderBy(orderField, direction), limit(pageSize)];
        if (scanCursor) cons.push(startAfter(scanCursor));
        // eslint-disable-next-line no-await-in-loop
        const querySnapshot = await getDocs(query(collection(db, name), ...cons));
        if (querySnapshot.size < pageSize) exhausted = true;
        querySnapshot.forEach((item) => {
          scanCursor = item;
          const data = { id: item.id, ...item.data() };
          if (!f || f(data)) kept.push({ snap: item, data });
        });
      }

      const pageItems = kept.slice(0, pageSize);
      const data = pageItems.map((k) => k.data);
      const lastSnap = pageItems.length ? pageItems[pageItems.length - 1].snap : scanCursor;
      // more pages exist if we over-collected, or filled the page without exhausting
      const more = kept.length > pageSize || (kept.length === pageSize && !exhausted);

      pagesRef.current = { ...pagesRef.current, [pageIndex]: data };
      cursorsRef.current = { ...cursorsRef.current, [pageIndex]: lastSnap };
      moreRef.current = { ...moreRef.current, [pageIndex]: more };
      setPage(pageIndex);
      bump();
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [db, name, skip, pageSize, direction, bump]);

  // (re)load from the first page on mount, when un-skipped, or when the filter
  // identity changes (e.g. language). Refs are cleared synchronously so the
  // immediate fetchPage(0) does not hit a stale cache.
  useEffect(() => {
    if (skip) return;
    pagesRef.current = {};
    cursorsRef.current = {};
    moreRef.current = {};
    setPage(0);
    fetchPage(0);
  }, [db, name, skip, filterKey, fetchPage]);

  const loadedPages = Object.keys(pagesRef.current).map(Number);
  const maxLoaded = loadedPages.length ? Math.max(...loadedPages) : 0;
  const hasMoreAtMax = !!moreRef.current[maxLoaded];
  // count of numbered buttons to render (last one is the not-yet-loaded "next")
  const pageCount = maxLoaded + 1 + (hasMoreAtMax ? 1 : 0);

  const goToPage = useCallback((pageIndex) => {
    const loaded = Object.keys(pagesRef.current).map(Number);
    const max = loaded.length ? Math.max(...loaded) : 0;
    const more = !!moreRef.current[max];
    if (pageIndex < 0 || pageIndex > max + 1) return;
    if (pageIndex === max + 1 && !more) return;
    fetchPage(pageIndex);
  }, [fetchPage]);

  return {
    data: pagesRef.current[page] || null,
    loading,
    error,
    page,
    pageCount,
    hasNext: page < maxLoaded || (page === maxLoaded && hasMoreAtMax),
    hasPrev: page > 0,
    goToPage,
  };
}

export default FirebaseProvider;
